import { useCallback } from "react";

export default function useMembersPanelActions({
  authFetch,
  canManageUsers,
  canManageMemberships,
  selectedUser,
  membershipUser,
  membershipForm,
  setSelectedUser,
  setDeleteOpen,
  setActionLoadingId,
  setBanner,
  setUsers,
  setMembershipOpen,
  setMembershipLoading,
  setMembershipSubmitting,
  setMembershipUser,
  setMemberMemberships,
  setActiveMembership,
  setPlans,
  setMembershipForm,
  resetMembershipForm,
}) {
  const handleToggleStatus = useCallback(
    async (member) => {
      if (!canManageUsers) return;

      setActionLoadingId(member.id);
      setBanner({ type: "", message: "" });

      try {
        const response = await authFetch(
          `/api/gym/admin/users/${member.id}/toggle-status/`,
          {
            method: "PATCH",
            body: JSON.stringify({
              is_active: !member.is_active,
            }),
          }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          setBanner({
            type: "error",
            message: data?.error || "No se pudo actualizar el estado",
          });
          return;
        }

        setUsers((prev) =>
          prev.map((item) =>
            item.id === member.id
              ? { ...item, is_active: data.user.is_active }
              : item
          )
        );

        setBanner({
          type: "success",
          message: data?.message || "Estado actualizado correctamente",
        });
      } catch {
        setBanner({
          type: "error",
          message: "Ocurrió un error al actualizar el estado",
        });
      } finally {
        setActionLoadingId("");
      }
    },
    [authFetch, canManageUsers, setActionLoadingId, setBanner, setUsers]
  );

  const openDeleteModal = useCallback(
    (member) => {
      if (!canManageUsers) return;
      setSelectedUser(member);
      setDeleteOpen(true);
    },
    [canManageUsers, setSelectedUser, setDeleteOpen]
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteOpen(false);
    setSelectedUser(null);
  }, [setDeleteOpen, setSelectedUser]);

  const handleDeleteUser = useCallback(async () => {
    if (!canManageUsers || !selectedUser) return;

    setActionLoadingId(selectedUser.id);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(
        `/api/gym/admin/users/${selectedUser.id}/delete/`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo eliminar el usuario",
        });
        return;
      }

      setUsers((prev) =>
        prev.filter((item) => item.id !== selectedUser.id)
      );

      setBanner({
        type: "success",
        message: data?.message || "Usuario eliminado correctamente",
      });

      setDeleteOpen(false);
      setSelectedUser(null);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al eliminar el usuario",
      });
    } finally {
      setActionLoadingId("");
    }
  }, [
    authFetch,
    canManageUsers,
    selectedUser,
    setActionLoadingId,
    setBanner,
    setUsers,
    setDeleteOpen,
    setSelectedUser,
  ]);

  const loadMemberMemberships = useCallback(
    async (memberId) => {
      const [plansResponse, membershipsResponse, activeResponse] =
        await Promise.all([
          authFetch("/api/memberships/plans/", { method: "GET" }),
          authFetch(
            `/api/memberships/members/${memberId}/memberships/`,
            { method: "GET" }
          ),
          authFetch(
            `/api/memberships/members/${memberId}/active-membership/`,
            { method: "GET" }
          ),
        ]);

      const plansData = await plansResponse.json().catch(() => []);
      const membershipsData =
        await membershipsResponse.json().catch(() => []);
      const activeData = await activeResponse.json().catch(() => ({}));

      if (!plansResponse.ok)
        throw new Error("No se pudieron cargar los planes");

      if (!membershipsResponse.ok)
        throw new Error("No se pudieron cargar las membresías");

      if (!activeResponse.ok)
        throw new Error("No se pudo cargar la membresía activa");

      setPlans(
        Array.isArray(plansData)
          ? plansData.filter((item) => item.is_active)
          : []
      );

      setMemberMemberships(
        Array.isArray(membershipsData) ? membershipsData : []
      );

      setActiveMembership(activeData?.membership || null);
    },
    [authFetch, setPlans, setMemberMemberships, setActiveMembership]
  );

  const openMembershipModal = useCallback(
    async (member) => {
      if (!canManageMemberships) return;

      setMembershipUser(member);
      setMembershipOpen(true);
      setMembershipLoading(true);
      resetMembershipForm();
      setBanner({ type: "", message: "" });

      try {
        await loadMemberMemberships(member.id);
      } catch (error) {
        setBanner({
          type: "error",
          message:
            error.message ||
            "No se pudo cargar la información de membresías",
        });
      } finally {
        setMembershipLoading(false);
      }
    },
    [
      canManageMemberships,
      setMembershipUser,
      setMembershipOpen,
      setMembershipLoading,
      resetMembershipForm,
      setBanner,
      loadMemberMemberships,
    ]
  );

  const closeMembershipModal = useCallback(() => {
    setMembershipOpen(false);
    setMembershipUser(null);
    setMemberMemberships([]);
    setActiveMembership(null);
    setPlans([]);
    resetMembershipForm();
  }, [
    setMembershipOpen,
    setMembershipUser,
    setMemberMemberships,
    setActiveMembership,
    setPlans,
    resetMembershipForm,
  ]);

  const handleMembershipFieldChange = useCallback(
    (field, value) => {
      setMembershipForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [setMembershipForm]
  );

  const handleMembershipAccessToggle = useCallback(
    (access) => {
      setMembershipForm((prev) => {
        const exists = prev.selected_accesses.includes(access);

        return {
          ...prev,
          selected_accesses: exists
            ? prev.selected_accesses.filter(
                (item) => item !== access
              )
            : [...prev.selected_accesses, access],
        };
      });
    },
    [setMembershipForm]
  );

  const handleAssignMembership = useCallback(async () => {
    if (!canManageMemberships || !membershipUser) return;

    setMembershipSubmitting(true);
    setBanner({ type: "", message: "" });

    try {
      const payload = {
        member_id: membershipUser.id,
        plan_id: membershipForm.plan_id,
        selected_accesses: membershipForm.selected_accesses,
        start_date: membershipForm.start_date,
        end_date: membershipForm.end_date,
        price_paid: membershipForm.price_paid,
        notes: membershipForm.notes,
      };

      if (membershipForm.visits_total !== "") {
        payload.visits_total = Number(
          membershipForm.visits_total
        );
      }

      const response = await authFetch(
        "/api/memberships/memberships/create/",
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message:
            data?.error ||
            "No se pudo asignar la membresía",
        });
        return;
      }

      await loadMemberMemberships(membershipUser.id);

      resetMembershipForm();

      setBanner({
        type: "success",
        message:
          data?.message ||
          "Membresía asignada correctamente",
      });
    } catch {
      setBanner({
        type: "error",
        message:
          "Ocurrió un error al asignar la membresía",
      });
    } finally {
      setMembershipSubmitting(false);
    }
  }, [
    authFetch,
    canManageMemberships,
    membershipUser,
    membershipForm,
    setMembershipSubmitting,
    setBanner,
    loadMemberMemberships,
    resetMembershipForm,
  ]);

  const handleDeleteMembership = useCallback(
    async (membershipId) => {
      if (!canManageMemberships || !membershipUser) return;

      setMembershipLoading(true);
      setBanner({ type: "", message: "" });

      try {
        const response = await authFetch(
          `/api/memberships/memberships/${membershipId}/delete/`,
          {
            method: "DELETE",
          }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          setBanner({
            type: "error",
            message:
              data?.error ||
              "No se pudo eliminar la membresía",
          });
          return;
        }

        await loadMemberMemberships(
          membershipUser.id
        );

        setBanner({
          type: "success",
          message:
            data?.message ||
            "Membresía eliminada correctamente",
        });
      } catch {
        setBanner({
          type: "error",
          message:
            "Ocurrió un error al eliminar la membresía",
        });
      } finally {
        setMembershipLoading(false);
      }
    },
    [
      authFetch,
      canManageMemberships,
      membershipUser,
      setMembershipLoading,
      setBanner,
      loadMemberMemberships,
    ]
  );

  return {
    handleToggleStatus,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteUser,
    openMembershipModal,
    closeMembershipModal,
    handleMembershipFieldChange,
    handleMembershipAccessToggle,
    handleAssignMembership,
    handleDeleteMembership,
  };
}